import {
  MemberEntriesCards,
  MemberEntriesFilters
} from '@bo-components/members/entries';
import { useCallback, useState } from 'react';
import {
  useMemberEntries,
  useMemberEntriesFilters,
  useMemberEntryModal
} from '@bo-hooks/members';
import { MemberEntryModal } from '@library/components/modals/MemberEntryModal';
import type { NewWagerSearchParam } from '@library/types/newWagers';
import { getNewWagerById } from '@bo-utils/newWagers';
import { selectMemberActionsAccessPermissions } from '@bo-selectors/members/memberActionPermissions';
import { useInfiniteScroll } from '@bo-hooks/useInfiniteScroll';
import { useLeagues } from '@bo-hooks/useLeagues';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

export const MemberEntries = () => {
  const { memberId } = useParams<{ memberId?: string }>();
  const [page, setPage] = useState(1);

  const { entriesSearchFilters, onChangeEntriesFilter, onResetEntriesFilters } =
    useMemberEntriesFilters();

  const {
    count,
    estimatedTotal,
    isNewWagersLoading,
    newWagersData,
    noDataResults
  } = useMemberEntries({
    entriesSearchFilters,
    memberId,
    page
  });

  const { onCancelModal, onShowModal, selectedNewWager, showModal } =
    useMemberEntryModal({ memberId });

  const { hasAccessMemberRefundEntry } = useSelector(
    selectMemberActionsAccessPermissions
  );

  const { infScrollRef, showInfScrollRef } = useInfiniteScroll({
    count,
    estimatedTotal,
    isLoading: isNewWagersLoading,
    nextPage: () => setPage((prevPage) => prevPage + 1)
  });

  const { leagueOptions } = useLeagues();

  const handleShowModal = useCallback(
    (newWagerId: string) => {
      const newWager = getNewWagerById(newWagersData, newWagerId);

      if (newWager) {
        onShowModal(newWager);
      }
    },
    [newWagersData, onShowModal]
  );

  const handleChangeEntriesFilter = useCallback(
    ({ key, value }: NewWagerSearchParam) => {
      onChangeEntriesFilter({ key, value });
      setPage(1);
    },
    [onChangeEntriesFilter]
  );

  const handleResetEntriesFilters = useCallback(() => {
    onResetEntriesFilters();
    setPage(1);
  }, [onResetEntriesFilters]);

  return (
    <section className="flex flex-col gap-4 px-6" data-testid="member-entries">
      <MemberEntriesFilters
        entriesSearchFilters={entriesSearchFilters}
        leagueOptions={leagueOptions}
        onChangeEntriesFilter={handleChangeEntriesFilter}
        onResetEntriesFilters={handleResetEntriesFilters}
      />
      <MemberEntriesCards
        isNewWagersLoading={isNewWagersLoading}
        newWagersData={newWagersData}
        noDataResults={noDataResults}
        onShowModal={handleShowModal}
      />
      {showInfScrollRef && <div ref={infScrollRef} />}
      {showModal && (
        <MemberEntryModal
          canRefundEntry={hasAccessMemberRefundEntry}
          isOpen
          onCancelModal={onCancelModal}
          selectedNewWager={selectedNewWager}
        />
      )}
    </section>
  );
};
